import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import { orders } from "@/api/orders";
import { portfolio } from "@/api/portfolio";
import { margin } from "@/api/margin";
import { Modal } from "@/components/Modal";
import { useActiveAccount } from "@/components/ActiveAccount";
import { useToast } from "@/components/Toast";
import { useWsSubscribe } from "@/ws/WebSocketProvider";
import { Button, EmptyState, FormError, Input, Label, Skeleton } from "@/components/ui";

export function PositionsPane({ onSelect }: { onSelect?: (symbol: string) => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { active } = useActiveAccount();
  const [modifyTarget, setModifyTarget] = useState<{ id: string; symbol: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["portfolio"],
    queryFn: () => portfolio.get(),
    refetchInterval: 30_000,
  });
  const marginQ = useQuery({
    queryKey: ["margin", active?.id],
    queryFn: () => margin.get(active!.id),
    enabled: !!active,
    refetchInterval: 5_000,
  });
  useWsSubscribe("fill", () => {
    qc.invalidateQueries({ queryKey: ["portfolio"] });
    qc.invalidateQueries({ queryKey: ["margin"] });
  });
  useWsSubscribe("margin_event", () => {
    qc.invalidateQueries({ queryKey: ["margin"] });
  });

  const closePos = useMutation({
    mutationFn: (positionId: string) => orders.close(positionId),
    onSuccess: () => {
      toast.push("Close submitted", "warn");
      qc.invalidateQueries({ queryKey: ["portfolio"] });
    },
    onError: (e) => toast.push(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e), "error"),
  });

  const flatten = useMutation({
    mutationFn: () => orders.flatten(),
    onSuccess: () => {
      toast.push("Flatten submitted", "warn");
      qc.invalidateQueries({ queryKey: ["portfolio"] });
    },
    onError: (e) => toast.push(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e), "error"),
  });

  if (isLoading) {
    return (
      <div className="space-y-1.5 p-2">
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} className="h-5 w-full" />
        ))}
      </div>
    );
  }
  if (!data || data.positions.length === 0) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex-1">
          <EmptyState title="No open positions" description="Place an order from the ticket pane to take a position." />
        </div>
        {marginQ.data && <MarginFooter snap={marginQ.data} />}
      </div>
    );
  }
  return (
    <div className="flex h-full flex-col text-2xs">
      <div className="flex items-center justify-between border-b border-term-border bg-term-bg px-2 py-1">
        <span className="uppercase tracking-wider text-term-mute">{data.positions.length} open</span>
        <Button
          variant="danger"
          onClick={() => {
            if (confirm(`Flatten all ${data.positions.length} positions?`)) flatten.mutate();
          }}
          disabled={flatten.isPending}
        >
          Flatten all
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto">
        <table className="w-full">
          <thead className="sticky top-0 bg-term-bg text-term-mute">
            <tr>
              <th className="px-2 py-1 text-left font-medium uppercase tracking-wider">Sym</th>
              <th className="px-2 py-1 text-left font-medium uppercase tracking-wider">Side</th>
              <th className="px-2 py-1 text-right font-medium uppercase tracking-wider">Qty</th>
              <th className="px-2 py-1 text-right font-medium uppercase tracking-wider">Avg</th>
              <th className="px-2 py-1 text-right font-medium uppercase tracking-wider">UPnL</th>
              <th className="px-2 py-1 text-right font-medium uppercase tracking-wider">RPnL</th>
              <th className="px-2 py-1 text-right font-medium uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.positions.map((p) => (
              <tr
                key={p.id ?? p.instrument_id}
                className="border-t border-term-border hover:bg-term-elev"
              >
                <td className="px-2 py-1 font-medium text-term-fg cursor-pointer" onClick={() => onSelect?.(p.symbol)}>
                  {p.symbol}
                </td>
                <td className={`px-2 py-1 ${p.side === "BUY" ? "up" : "down"}`}>{p.side}</td>
                <td className="px-2 py-1 text-right tnum text-term-dim">{p.quantity}</td>
                <td className="px-2 py-1 text-right tnum text-term-dim">
                  {Number(p.avg_entry_price ?? 0).toFixed(p.symbol.includes("/") ? (p.symbol.endsWith("JPY") ? 3 : 5) : 2)}
                </td>
                <td className={`px-2 py-1 text-right tnum ${p.unrealized_pnl >= 0 ? "up" : "down"}`}>
                  {p.unrealized_pnl.toFixed(2)}
                </td>
                <td className={`px-2 py-1 text-right tnum ${p.realized_pnl >= 0 ? "up" : "down"}`}>
                  {p.realized_pnl.toFixed(2)}
                </td>
                <td className="px-2 py-1 text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      onClick={() => setModifyTarget({ id: p.id, symbol: p.symbol })}
                    >
                      SL/TP
                    </Button>
                    <Button
                      variant="danger"
                      onClick={() => {
                        if (confirm(`Close ${p.side} ${p.quantity} ${p.symbol}?`)) closePos.mutate(p.id);
                      }}
                      disabled={closePos.isPending}
                    >
                      Close
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {marginQ.data && <MarginFooter snap={marginQ.data} />}
      {modifyTarget && (
        <ModifyPositionModal
          target={modifyTarget}
          onClose={() => setModifyTarget(null)}
        />
      )}
    </div>
  );
}


function ModifyPositionModal({
  target,
  onClose,
}: {
  target: { id: string; symbol: string };
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [sl, setSl] = useState("");
  const [tp, setTp] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () =>
      orders.modify(target.id, {
        stop_loss: sl ? Number(sl) : null,
        take_profit: tp ? Number(tp) : null,
      }),
    onSuccess: () => {
      toast.push(`SL/TP updated for ${target.symbol}`, "success");
      qc.invalidateQueries({ queryKey: ["portfolio"] });
      onClose();
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });

  return (
    <Modal open onClose={onClose} title={`SL / TP · ${target.symbol}`}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          submit.mutate();
        }}
        className="space-y-3"
      >
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Stop loss</Label>
            <Input
              type="number"
              step="any"
              value={sl}
              onChange={(e) => setSl(e.target.value)}
              placeholder="leave blank to clear"
            />
          </div>
          <div>
            <Label>Take profit</Label>
            <Input
              type="number"
              step="any"
              value={tp}
              onChange={(e) => setTp(e.target.value)}
              placeholder="leave blank to clear"
            />
          </div>
        </div>
        <FormError>{err}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={submit.isPending}>Apply</Button>
        </div>
      </form>
    </Modal>
  );
}


function MarginFooter({
  snap,
}: {
  snap: {
    equity: string;
    margin_used: string;
    margin_level_pct: string | null;
    mc_level_pct: string;
    so_level_pct: string;
  };
}) {
  const level = snap.margin_level_pct == null ? null : Number(snap.margin_level_pct);
  const mc = Number(snap.mc_level_pct);
  const so = Number(snap.so_level_pct);
  const tone =
    level != null && level <= so
      ? "border-red-700 bg-red-900/30 text-red-200"
      : level != null && level <= mc
        ? "border-amber-700 bg-amber-900/30 text-amber-200"
        : "border-term-border bg-term-elev text-term-dim";
  return (
    <div className={`flex items-center justify-between gap-3 border-t px-2 py-1 text-2xs ${tone}`}>
      <span>
        <span className="uppercase tracking-wider">Equity</span>{" "}
        <span className="font-mono text-term-fg">{snap.equity}</span>
      </span>
      <span>
        <span className="uppercase tracking-wider">Margin</span>{" "}
        <span className="font-mono">{snap.margin_used}</span>
      </span>
      <span>
        <span className="uppercase tracking-wider">Level</span>{" "}
        <span className="font-mono font-semibold">
          {level == null ? "∞" : `${level.toFixed(2)}%`}
        </span>
      </span>
    </div>
  );
}
